import { useCallback, useEffect, useState } from 'react';
import {
    acknowledgeIntegrityFinding,
    fetchIntegrityFindings,
    type StoredIntegrityFinding,
} from '../services/integrity/integrityService';

/**
 * Laedt die Befunde des Integritaetswaechters (Tabelle `integrity_findings`,
 * MIGRATIONS.md Nr. 56) und bietet den einen Eingriff an, den es hier zu
 * bedienen gibt: quittieren.
 *
 * REPARIERT wird nicht hier, sondern im Projekt. Der Waechter ist ein
 * Gedaechtnis, kein Werkzeug: er merkt sich, seit wann etwas nicht stimmt und
 * wer gesagt hat, dass es so in Ordnung ist. Ein zweiter Reparaturweg neben
 * dem im Projekt waere eine zweite Wahrheit fuer dieselbe Frage.
 *
 * Geladen wird IMMER alles, was der Nutzer sehen darf (die SELECT-Policy
 * trennt selbst, siehe fetchIntegrityFindings). Wer nur einen Ausschnitt
 * anzeigen will, filtert `findings` — nicht die Abfrage.
 */
export const useIntegrityFindings = (enabled: boolean = true) => {
    const [findings, setFindings] = useState<StoredIntegrityFinding[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [busyId, setBusyId] = useState<string | null>(null);

    /**
     * `hintergrund` laedt neu, ohne die Liste gegen einen Spinner zu
     * tauschen — nach einer Quittierung soll die Ansicht nicht flackern und
     * der Nutzer die Zeile weiter im Blick behalten.
     */
    const reload = useCallback(async (hintergrund = false) => {
        if (!enabled) { setFindings([]); setIsLoading(false); return; }
        if (!hintergrund) setIsLoading(true);
        try {
            setFindings(await fetchIntegrityFindings());
            setError(null);
        } catch (e) {
            // Die Liste bleibt stehen, wie sie war. Sie durch eine leere zu
            // ersetzen hiesse in dieser Ansicht "nichts gefunden" — die eine
            // Aussage, die ein Ladefehler gerade NICHT traegt.
            setError(e instanceof Error ? e.message : 'Die Befunde konnten nicht geladen werden.');
        } finally {
            if (!hintergrund) setIsLoading(false);
        }
    }, [enabled]);

    useEffect(() => { void reload(); }, [reload]);

    /**
     * Quittieren. Erst NACH erfolgreicher RPC den Stand uebernehmen — ein
     * optimistisches Umschreiben haette dem Nutzer bei einem Fehler genau das
     * vorgespielt, was dieser ganze Vorgang abschaffen soll: erledigt
     * aussehen, ohne erledigt zu sein.
     *
     * Und uebernommen wird der Stand aus der DATENBANK, nicht ein hier
     * zusammengesetzter: welchen Fingerabdruck die Quittierung festhaelt,
     * entscheidet die RPC aus der Zeile. Haette der Hook ihn geraten, stuende
     * bei einem zwischenzeitlich fortgeschriebenen Befund die falsche
     * Vergleichsgroesse in der Liste — und der wichtigste Zustand der ganzen
     * Ansicht, „wieder aufgelebt", waere ausgerechnet dort blind.
     *
     * Die Zeile verschwindet dabei NICHT aus `findings`: quittiert heisst
     * nicht erledigt. Sie wird in der Ansicht nur ausgeblendet, solange der
     * Schalter zu ist.
     */
    const acknowledge = useCallback(async (findingId: string, reason: string) => {
        setBusyId(findingId);
        setError(null);
        try {
            await acknowledgeIntegrityFinding(findingId, reason);
            await reload(true);
        } catch (e: any) {
            setError(e?.message || 'Der Befund konnte nicht quittiert werden.');
            throw e;
        } finally {
            setBusyId(null);
        }
    }, [reload]);

    return { findings, isLoading, error, busyId, reload, acknowledge };
};
